/**
 * ワークスペースドメインスキーマ — エンティティ + CRUD
 */
import { z } from 'zod';
import { ModelSelectionSchema } from './base-schemas';
import { WorkspaceHooksSchema } from './workspace-hooks-schemas';
/** LLM プリセット名のスキーマ */
export const LlmPresetSchema = z.enum(['creative', 'balanced', 'precise']);
// ========================================
// コンテキスト圧縮設定
// ========================================
/**
 * コンテキスト圧縮設定スキーマ
 */
export const CompressionConfigSchema = z.object({
    enabled: z.boolean().default(true),
    triggerThresholdPercent: z.number().min(10).max(100).default(80),
    recentTurnsToKeep: z.number().min(1).max(50).default(10),
});
// ========================================
// Advisor 設定
// ========================================
/**
 * Advisor 設定スキーマ
 * null: Advisor 無効、オブジェクト: Advisor 有効
 */
export const AdvisorConfigSchema = z
    .object({
    /** Advisor モデルの AI プロバイダー */
    advisorProvider: z.enum(['openai', 'anthropic', 'bedrock', 'google']).default('bedrock'),
    /** Advisor モデル ID（プロバイダー固有の ID） */
    advisorModelName: z.string().min(1),
    /** リクエストあたりの最大呼び出し回数 */
    maxUsesPerRequest: z.number().int().positive().optional().default(3),
    /** 会話あたりの最大呼び出し回数 */
    maxUsesPerConversation: z.number().int().positive().optional().default(10),
    /** Advisor モデルの Reasoning モード（省略時: Executor と同じ） */
    advisorReasoningMode: z.enum(['on', 'off']).optional(),
})
    .nullable();
// ========================================
// 教訓（Lessons Learned）設定
// ========================================
/**
 * 教訓自動記録設定スキーマ
 * null: 教訓機能無効、オブジェクト: 教訓機能有効
 */
export const LessonsConfigSchema = z
    .object({
    /** 教訓自動記録の有効/無効 */
    enabled: z.boolean(),
    /** LESSON.md のエントリ上限（デフォルト: 20、範囲: 5〜100） */
    maxEntries: z.number().int().min(5).max(100).default(20),
})
    .nullable();
// ========================================
// 承認バイパス設定
// ========================================
/**
 * ワークスペース単位の承認バイパス設定スキーマ
 * null: バイパス無効、オブジェクト: 設定あり（enabled で有効/無効を切替）
 */
export const ApprovalBypassConfigSchema = z
    .object({
    /** バイパスが有効かどうか */
    enabled: z.boolean(),
})
    .nullable();
// ========================================
// サンプルプロンプト
// ========================================
/** サンプルプロンプト */
export const SamplePromptSchema = z.object({
    title: z.string().min(1),
    body: z.string().min(1),
    autoSend: z.boolean().optional().default(false),
});
// ========================================
// 初期チャットメッセージ
// ========================================
/** 初期チャットメッセージの最大文字数 */
export const INITIAL_CHAT_MESSAGE_MAX_LENGTH = 4000;
/**
 * 初期チャットメッセージスキーマ
 * - null または非空文字列を保持
 * - 空文字・空白のみは null に正規化
 * - 最大長超過は Zod エラー
 */
export const InitialChatMessageSchema = z
    .union([z.string().max(INITIAL_CHAT_MESSAGE_MAX_LENGTH), z.null()])
    .transform((value) => {
    if (value === null)
        return null;
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : value;
});
// ========================================
// ワークスペースドキュメント
// ========================================
/** ワークスペースドキュメントメタデータ */
export const WorkspaceDocumentSchema = z.object({
    fileName: z.string(),
    fileSize: z.number(),
    mimeType: z.string(),
});
// ========================================
// エンティティ
// ========================================
/**
 * ワークスペース
 */
export const WorkspaceSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    rule: z.string(),
    /** null: 全て有効（フィルタなし）、[]: 全て無効、string[]: ホワイトリスト */
    enabledTools: z.array(z.string()).nullable().default([]),
    enabledSubAgents: z.array(z.string()).nullable().default([]),
    enabledSkills: z.array(z.string()).nullable().default([]),
    enabledExternalAgents: z.array(z.string()).nullable().default([]),
    enabledSecrets: z.array(z.string()).nullable().default([]),
    /** null: 全て有効（フィルタなし）、[]: 全て無効、string[]: ホワイトリスト */
    enabledVolumeMounts: z.array(z.string()).nullable().default([]),
    containerSandboxEnabled: z.boolean().optional().default(false),
    toolResultTruncateSize: z.number().nullable().optional().default(null),
    maxSteps: z.number().nullable().optional().default(null),
    llmPreset: LlmPresetSchema.nullable().optional().default(null),
    preferredModel: ModelSelectionSchema.nullable().optional().default(null),
    compressionConfig: CompressionConfigSchema.nullable().optional().default(null),
    envVars: z.record(z.string(), z.string()).nullable().optional().default(null),
    hooks: WorkspaceHooksSchema.nullable().optional().default(null),
    samplePrompts: z.array(SamplePromptSchema).nullable().optional().default(null),
    initialChatMessage: InitialChatMessageSchema.optional().default(null),
    advisorConfig: AdvisorConfigSchema.optional().default(null),
    lessonsConfig: LessonsConfigSchema.optional().default(null),
    approvalBypassConfig: ApprovalBypassConfigSchema.optional().default(null),
    documents: z.array(WorkspaceDocumentSchema).optional().default([]),
    isFavorite: z.boolean(),
    favoritedAt: z.number().nullable(),
    isDefault: z.boolean(),
    createdAt: z.number(),
    updatedAt: z.number(),
});
/**
 * ワークスペース削除タスクアクション
 */
export const WorkspaceDeleteTaskActionSchema = z.enum(['move', 'delete']);
// ========================================
// パラメータ（共通フィールドを DRY 化）
// ========================================
const WorkspaceSettingsFieldsSchema = z.object({
    description: z.string().optional(),
    rule: z.string().optional(),
    enabledTools: z.array(z.string()).nullable().optional(),
    enabledSubAgents: z.array(z.string()).nullable().optional(),
    enabledSkills: z.array(z.string()).nullable().optional(),
    enabledExternalAgents: z.array(z.string()).nullable().optional(),
    enabledSecrets: z.array(z.string()).nullable().optional(),
    enabledVolumeMounts: z.array(z.string()).nullable().optional(),
    containerSandboxEnabled: z.boolean().optional(),
    toolResultTruncateSize: z.number().nullable().optional(),
    maxSteps: z.number().nullable().optional(),
    llmPreset: LlmPresetSchema.nullable().optional(),
    preferredModel: ModelSelectionSchema.nullable().optional(),
    compressionConfig: CompressionConfigSchema.nullable().optional(),
    envVars: z.record(z.string(), z.string()).nullable().optional(),
    hooks: WorkspaceHooksSchema.nullable().optional(),
    samplePrompts: z.array(SamplePromptSchema).nullable().optional(),
    initialChatMessage: InitialChatMessageSchema.optional(),
    advisorConfig: AdvisorConfigSchema.optional(),
    lessonsConfig: LessonsConfigSchema.optional(),
    approvalBypassConfig: ApprovalBypassConfigSchema.optional(),
    documents: z.array(WorkspaceDocumentSchema).optional(),
});
/**
 * ワークスペース作成パラメータ
 */
export const WorkspaceCreateParamsSchema = WorkspaceSettingsFieldsSchema.extend({
    name: z.string().min(1, 'name is required'),
});
/**
 * ワークスペース更新パラメータ
 */
export const WorkspaceUpdateParamsSchema = WorkspaceSettingsFieldsSchema.extend({
    name: z.string().optional(),
});
// ========================================
// リクエスト
// ========================================
/**
 * POST /workspaces リクエスト
 */
export const CreateWorkspaceRequestSchema = z.object({
    workspace: WorkspaceCreateParamsSchema,
});
/**
 * PUT /workspaces/{id} リクエスト
 */
export const UpdateWorkspaceRequestSchema = z.object({
    workspace: WorkspaceUpdateParamsSchema,
});
// ========================================
// レスポンス
// ========================================
/**
 * GET /workspaces レスポンス
 */
export const WorkspaceListResponseSchema = z.object({
    workspaces: z.array(WorkspaceSchema),
});
/**
 * GET /workspaces/{id} レスポンス
 */
export const WorkspaceDetailResponseSchema = z.object({
    workspace: WorkspaceSchema,
});
/**
 * POST /workspaces レスポンス
 */
export const CreateWorkspaceResponseSchema = z.object({
    workspace: WorkspaceSchema,
});
/**
 * PUT /workspaces/{id} レスポンス
 */
export const UpdateWorkspaceResponseSchema = z.object({
    workspace: WorkspaceSchema,
});
/**
 * PATCH /workspaces/{id}/favorite レスポンス
 */
export const ToggleFavoriteResponseSchema = z.object({
    workspace: WorkspaceSchema,
});
/**
 * PATCH /workspaces/{id}/default レスポンス
 */
export const SetAsDefaultResponseSchema = z.object({
    workspace: WorkspaceSchema,
});
// ========================================
// クエリ
// ========================================
/**
 * DELETE /workspaces/{id} クエリパラメータ
 */
export const DeleteWorkspaceParamsSchema = z.object({
    taskAction: WorkspaceDeleteTaskActionSchema.optional(),
    targetWorkspaceId: z.string().optional(),
});
//# sourceMappingURL=workspace-schemas.js.map